/**
 * Working out which aircraft a file is about.
 *
 * A GA callsign usually *is* the registration with the hyphen dropped, but
 * "usually" is doing real work there: `ANZ534` is a callsign and not a
 * registration, and guessing wrong attaches a flight to the wrong aircraft.
 * So the rule is narrow and explicit rather than clever.
 */

/**
 * National prefixes that hyphenate after two characters and then run three
 * letters. New Zealand (`ZK`) is the one that matters here; Australia (`VH`) is
 * one ditch crossing away and costs nothing to include.
 */
const HYPHEN_AFTER_TWO = /^(ZK|VH)([A-Z]{3})$/;

/**
 * Normalise a registration to its canonical hyphenated form, uppercase.
 *
 * `aircraft.registration` is a unique index, so `ZKDFH` and `zk-dfh` arriving
 * from two different file formats must land on one record, not two.
 */
export function normaliseRegistration(raw: string): string {
  const compact = raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
  const match = HYPHEN_AFTER_TWO.exec(compact);
  return match ? `${match[1] ?? ''}-${match[2] ?? ''}` : compact;
}

/**
 * Whether a callsign can be read as an aircraft registration.
 *
 * Deliberately conservative: a three-letter airline designator followed by
 * digits is a flight number, and a registration this app does not recognise is
 * better left as a callsign than filed against an invented aircraft.
 */
export function looksLikeRegistration(callsign: string): boolean {
  const compact = callsign.toUpperCase().replace(/[^A-Z0-9]/g, '');
  return HYPHEN_AFTER_TWO.test(compact);
}

/**
 * Pull the registration out of a callsign where it is safe to do so.
 *
 * @returns the hyphenated registration, or `undefined` if the callsign is not
 *          one this app is prepared to claim is a registration.
 */
export function registrationFromCallsign(callsign: string | undefined): string | undefined {
  if (callsign === undefined || !looksLikeRegistration(callsign)) {
    return undefined;
  }
  return normaliseRegistration(callsign);
}

/**
 * Normalise an ICAO 24-bit address to six lowercase hex digits.
 *
 * Every ADS-B source writes it differently — `C827A6`, `c827a6`, `0xC827A6` —
 * and the aggregators' URLs only accept the bare lowercase form.
 * Six digits exactly: a shorter value is left-padded, because a leading zero is
 * routinely dropped by whatever produced it, and anything longer is not an
 * address at all.
 *
 * @returns the normalised address, or `''` if the input cannot be one.
 */
export function normaliseIcaoHex(raw: string): string {
  const compact = raw
    .trim()
    .toLowerCase()
    .replace(/^0x/, '')
    .replace(/[^0-9a-f]/g, '');
  if (compact === '' || compact.length > 6) {
    return '';
  }
  return compact.padStart(6, '0');
}
